import React from 'react';
import { Helmet } from 'react-helmet-async';
import { nafColor } from '@nafcore/theme';
import { DEFAULT_IMG_URL } from '@lib/constants';
import SchemaOrganization from '../structured-data/SchemaOrganization';
import favicon from '../../../assets/favicon.ico';
import faviconSvg from '../../../assets/favicon.svg?url';
import webmanifest from '../../../assets/manifest.webmanifest?url';
import { useSendGTMEventOnce } from '../../hooks/GTM/useSendGTMEventOnce';
import { useOpenInCms } from '../../hooks/useOpenInCms';
import ErrorBoundary from '../error-component/ErrorBoundary';
import { InternalSearchMetaDataType } from '../../../../types/InternalSearchMetaDataType';
import { InternalSearchMetaData } from './InternalSearchMetaData';
import { BackToTop } from '../BackToTop/BackToTop';
import { useWindowLocation } from '../../hooks/Window/useWindowLocation';
import MainContent from './MainContent';
import * as S from './Layout.styles';

interface Props {
  title?: string;
  description?: string;
  children?: any;
  url?: string;
  imgUrl?: string | undefined;
  imgWidth?: number;
  isHiddenFromSearch?: boolean;
  gtmArgs?: {
    page_type: string;
    pageCategory: string;
    contentId: string;
    author?: string;
    headline?: string;
    publishDate?: string;
    daysSincePublish?: number;
    articleWords?: string;
    isPaywall?: boolean;
    viewType?: string;
  };
  seoDescription?: string;
  seoTitle?: string;
  isGuidePage?: boolean;
  isMemberBenefitPage?: boolean;
  canonicalUrl?: string;
  disableCanonical?: boolean;
  internalSearchMetaData: InternalSearchMetaDataType;
  backToTopButton?: boolean;
  preview?: boolean;
}

export const LayoutWithMainContent = ({
  children,
  title,
  description,
  url,
  imgUrl,
  imgWidth,
  isHiddenFromSearch,
  gtmArgs,
  seoDescription,
  seoTitle,
  isGuidePage,
  isMemberBenefitPage,
  canonicalUrl,
  disableCanonical,
  internalSearchMetaData,
  backToTopButton,
  preview,
}: Props) => (
  <Layout
    title={title}
    description={description}
    url={url}
    imgUrl={imgUrl}
    imgWidth={imgWidth}
    isHiddenFromSearch={isHiddenFromSearch}
    gtmArgs={gtmArgs}
    seoDescription={seoDescription}
    seoTitle={seoTitle}
    canonicalUrl={canonicalUrl}
    disableCanonical={disableCanonical}
    internalSearchMetaData={internalSearchMetaData}
    preview={preview}
  >
    {backToTopButton && <BackToTop />}
    <S.ContentContainer $isGuidePage={isGuidePage}>
      <MainContent isGuidePage={isGuidePage} isMemberBenefitPage={isMemberBenefitPage}>
        {children}
      </MainContent>
    </S.ContentContainer>
  </Layout>
);

export const Layout = ({
  title = 'NAF - Norges Automobil-Forbund | NAF',
  description = '',
  children,
  url = '',
  imgUrl = DEFAULT_IMG_URL,
  imgWidth,
  gtmArgs,
  isHiddenFromSearch,
  seoTitle,
  seoDescription,
  canonicalUrl,
  disableCanonical,
  internalSearchMetaData,
  preview,
}: Omit<Props, 'userAccessibility'>) => {
  let eventArgs = gtmArgs;
  if (gtmArgs && !(gtmArgs.page_type === 'ArticlePage' || gtmArgs.page_type === 'CarTestPage')) {
    eventArgs = {
      ...gtmArgs,
      author: null,
      headline: null,
      publishDate: null,
      daysSincePublish: null,
      isPaywall: null,
      viewType: null,
    };
  }

  const windowPath = useWindowLocation();
  const finalCanonicalUrl = !disableCanonical ? canonicalUrl || windowPath : null;

  useSendGTMEventOnce(eventArgs || undefined, [gtmArgs]);

  useOpenInCms();

  return (
    <S.Wrapper>
      <Helmet>
        {seoTitle || title ? <title>{`${seoTitle || title} | NAF`}</title> : null}
        <base href="/" />
        <html lang="no" dir="ltr" />
        <meta name="language" content="nb_NO" />
        <meta charSet="utf-8" />
        <meta httpEquiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="og:type" content="website" />
        <meta name="theme-color" content={nafColor.signature.white} />
        <meta name="description" content={seoDescription || description} key="description" />
        {isHiddenFromSearch || preview ? (
          <meta name="robots" content="noindex" />
        ) : (
          <meta name="robots" content="max-image-preview:large" />
        )}
        <meta name="facebook-domain-verification" content="pgedkcgsbgqob438evbkr89u1c2qy5" />
        <meta property="og:title" content={`${title} | NAF`} key="og:title" />
        {url && <meta property="og:url" content={url} key="og:url" />}
        <meta property="og:description" content={seoDescription || description} key="og:description" />
        <meta property="og:image" content={imgUrl} />
        <meta property="og:image:secure_url" content={imgUrl} />
        {imgWidth && <meta property="og:image:width" content={`${imgWidth}`} />}
        <meta itemProp="image" content={imgUrl} />
        <link rel="icon" type="image/svg+xml" href={faviconSvg} />
        <link rel="icon" type="image/x-icon" href={favicon} />
        <link rel="manifest" href={webmanifest} crossOrigin="use-credentials" />
        <link rel="apple-touch-icon" href={favicon} />
        <link href="https://fonts.gstatic.com" rel="preconnect" crossOrigin="anonymous" />
        {!disableCanonical && finalCanonicalUrl && <link rel="canonical" href={finalCanonicalUrl} />}
        {InternalSearchMetaData({
          internalSearchMetaData,
          title: seoTitle || title,
          description: seoDescription || description,
        })}
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              name: seoTitle || title,
              description: seoDescription || description,
              image: imgUrl,
              url: canonicalUrl || url,
            }),
          }}
        />
      </Helmet>
      <SchemaOrganization />
      <ErrorBoundary>{children}</ErrorBoundary>
    </S.Wrapper>
  );
};
